/* eslint-disable @typescript-eslint/no-explicit-any */
import ReactDOM from 'react-dom';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import kebabCase from 'lodash.kebabcase';
import Icon from '3-components/Icon/Icon';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import VerticalBarChart from '3-components/Chart/VerticalBarChart';
import HorizontalBarChart from '3-components/Chart/HorizontalBarChart';
import GeoChart from '3-components/Chart/GeoChart';
import { BuildChartQueryFilter, IsRunningInFEDEnv } from './_ChartServices';
import debounce from 'lodash.debounce';

const APP_CLASS = 'chart-display-app';

type ChartVisualisationSplitterGroupProps = {
    title: string;
    identifier?: string;
    dependencySplitter?: string;
    dependencyOperator?: string;
    dependencyValue?: string;
    options: [
        {
            label: string;
            value: string;
        }
    ];
}

type ChartVisualisationSplitterProps = {
    groupTitle: string;
    groups: [ChartVisualisationSplitterGroupProps];
}

type ChartVisualisationProps = {
    label: string;
    prefixIcon?: string;
    src?: string;
    splitter?: ChartVisualisationSplitterProps;
    filter?: ChartVisualisationSplitterProps;
};

type ChartDisplayProps = {
    chartHeight?: number | string;
    maxValue?: number | string;
    arProps: {
        visualisation: [ChartVisualisationProps];
    };
    googleMapsKey?: string;
    chartDataType?: string;
    chartDataContent?: string;
}

const GetCompareSplitterLabel = (splitterValue?: string, splitterGroups?: [ChartVisualisationSplitterGroupProps]): string => {
    if (splitterValue && splitterGroups) {
        const compareSplitter = splitterGroups.filter(splitObj => {
            return splitObj.identifier === 'Compareby';
        });

        if (compareSplitter && compareSplitter.length > 0) {
            const selectedOption = compareSplitter[0].options.filter(opt => {
                return opt.value === splitterValue;
            });

            if (selectedOption && selectedOption.length > 0) {
                return selectedOption[0].label;
            }
        }
    }

    return '';
};

const RenderVisualisationButtons = (props: {
    visualisation: [ChartVisualisationProps];
    selectedVisualisation: ChartVisualisationProps;
    clickHandler?: (value: ChartVisualisationProps) => void;
}): JSX.Element => {
    const { visualisation, selectedVisualisation, clickHandler } = props;

    return (
        <div className="chart-control-group ar-chart-type">
            <div className="chart-control-group-title">Visualisation Type</div>
            <div className="chart-controls">
                {visualisation.map((value): JSX.Element => {
                    return (
                        <button
                            type="button"
                            className={`btn btn-size-sm ${selectedVisualisation.label === value.label ? 'btn-skin-1' : 'btn-skin-2'} visualisation-btn visualisation-btn-${kebabCase(value.label)}`}
                            onClick={(): void => {
                                // visualisationButtonHandler(value);
                                if (clickHandler) {
                                    clickHandler(value);
                                }
                            }}
                            key={`vis-type-btn-${value.label}`}
                        >
                            {value.prefixIcon && (
                                <Icon icon={value.prefixIcon} />
                            )}
                            {value.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const RenderVisualisationSplitter = (props: {
    selectedVisualisation: ChartVisualisationProps;
    splitterValue?: Record<string, string>;
    changeHandler?: (identifier: string, value: string) => void;
}): JSX.Element | null => {
    const { selectedVisualisation, splitterValue, changeHandler } = props;

    if (selectedVisualisation?.splitter?.groups && selectedVisualisation.splitter.groups.length > 0) {
        const { groupTitle, groups } = selectedVisualisation.splitter;

        // const { groupTitle, groups } = selectedVisualisation.filter;
        const groupToRender = groups.filter((group): boolean => {
            const { dependencyValue, dependencyOperator, dependencySplitter } = group;
            let dependencyMatch = !!dependencySplitter && ((splitterValue?.[dependencySplitter]) === dependencyValue);
            if (dependencyOperator === 'not eq') {
                dependencyMatch = !!dependencySplitter && ((splitterValue?.[dependencySplitter]) !== dependencyValue);
            }

            return (!dependencySplitter || dependencyMatch);
        });

        if (!groupToRender || groupToRender.length < 1) {
            return null;
        }

        return (
            <div className="chart-control-group ar-chart-split">
                <div className="chart-control-group-title">{groupTitle}</div>
                <div className="chart-controls">
                    {groupToRender.map((group): JSX.Element => {
                        const key = `ar-chart-split-${kebabCase(groupTitle)}-${kebabCase(group.identifier)}`;
                        const splitterValueForId = group.identifier && splitterValue?.[group.identifier];

                        return (
                            <div
                                className={`form-element element-size-md splitter-${kebabCase(group.title)}`}
                                key={key}
                            >
                                <div className="group-label">
                                    {group.title}
                                </div>
                                <div className="element-wrapper has-suffix-icon">
                                    <select
                                        value={splitterValueForId}
                                        onChange={(event): void => {
                                            if (group.identifier && changeHandler) {
                                                changeHandler(group.identifier, event.target.value);
                                            }
                                        }}
                                    >
                                        {group.options.map(option => {
                                            return (
                                                <option
                                                    value={option.value}
                                                    key={`split-option-${option.value}`}
                                                >
                                                    {option.label}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <Icon icon="chevron-down" className="suffix-icon" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return null;
};

const RenderVisualisationFilter = (props: {
    selectedVisualisation: ChartVisualisationProps;
    splitter?: Record<string, string>;
    filterValue?: Record<string, string>;
    changeHandler?: (identifier: string, value: string) => void;
}): JSX.Element | null => {
    const { selectedVisualisation, splitter, filterValue, changeHandler } = props;

    if (selectedVisualisation?.filter?.groups && selectedVisualisation.filter.groups.length > 0) {
        const { groupTitle, groups } = selectedVisualisation.filter;
        const groupToRender = groups.filter((group): boolean => {
            const { dependencyValue, dependencyOperator, dependencySplitter } = group;
            let dependencyMatch = !!dependencySplitter && ((splitter?.[dependencySplitter]) === dependencyValue);
            if (dependencyOperator === 'not eq') {
                dependencyMatch = !!dependencySplitter && ((splitter?.[dependencySplitter]) !== dependencyValue);
            }

            return (!dependencySplitter || dependencyMatch);
        });

        if (!groupToRender || groupToRender.length < 1) {
            return null;
        }

        return (
            <div className="chart-control-group ar-chart-filter">
                <div className="chart-control-group-title">{groupToRender?.length > 0 ? groupTitle : ''}</div>
                <div className="chart-controls">
                    {groupToRender.map(group => {
                        const { dependencyValue } = group;
                        const key = `ar-chart-split-${kebabCase(groupTitle)}-${kebabCase(group.title)}${dependencyValue ? `-${dependencyValue}` : ''}`;
                        const filterValueForId = group.identifier && filterValue?.[group.identifier];

                        return (
                            <div
                                className="form-element element-size-md"
                                key={key}
                            >
                                <div className="group-label">
                                    {group.title}
                                </div>
                                <div className="element-wrapper has-suffix-icon">
                                    <select
                                        value={filterValueForId}
                                        onChange={(event): void => {
                                            if (group.identifier && changeHandler) {
                                                changeHandler(group.identifier, event.target.value);
                                            }
                                        }}
                                    >
                                        {group.options.map(option => {
                                            return (
                                                <option
                                                    value={option.value}
                                                    key={`split-option-${option.value}`}
                                                >
                                                    {option.label}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <Icon icon="chevron-down" className="suffix-icon" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return null;
};

const RenderChartBody = (props: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    selectedVisualisation: ChartVisualisationProps;
    maxValue: number | string | undefined;
    expectedHeight: number | string | undefined;
    splitter?: Record<string, string>;
    filter?: Record<string, string>;
    googleMapsKey?: string;
    chartDataType?: string;
    chartDataContent?: string;
}): React.ReactElement => {
    const { data, selectedVisualisation, maxValue, expectedHeight, splitter, filter, googleMapsKey, chartDataType, chartDataContent } = props;
    // eslint-disable-next-line dot-notation
    let dataAxisLabel = GetCompareSplitterLabel(splitter?.['Compareby'], selectedVisualisation.splitter?.groups);

    let overrideMaxValue = maxValue;

    let decimalPlace = 0;
    let valueAxisLabel = 'Participation Rate';
    if (chartDataType === 'selfCollection') {
        // eslint-disable-next-line dot-notation
        const dataType = splitter ? splitter['dataType'] : undefined;
        if (dataType === '% of self-collection uptake') {
            overrideMaxValue = 100;
            valueAxisLabel = '% of Self Collected Tests';
            decimalPlace = 1;
        } else {
            valueAxisLabel = 'Number of Self Collected Tests';
        }
    } else if (chartDataType === 'colposcopy') {
        valueAxisLabel = '% of colposcopies completed within the selected time to assessment';
        overrideMaxValue = 100;
        decimalPlace = 1;
    } else if (chartDataType === 'colonoscopy') {
        valueAxisLabel = '% of colonoscopies completed within the selected time to assessment';
        overrideMaxValue = 100;
        decimalPlace = 1;
    } else if (chartDataType === 'screening') {
        if (chartDataContent === 'bowel' || chartDataContent === 'breast') {
            valueAxisLabel = 'Persons screened';
        } else {
            // cervical
            valueAxisLabel = 'Screening tests';
        }
    } else if (chartDataType === 'participation') {
        valueAxisLabel = 'Coverage Rate';
        decimalPlace = 1;

        if (chartDataContent === 'breast' || chartDataContent === 'bowel') {
            valueAxisLabel = 'Participation Rate';
        }

        if (selectedVisualisation?.label.toLowerCase() === 'trend') {
            dataAxisLabel = 'Time Period';
        }
    }

    if (selectedVisualisation?.label.toLowerCase() === 'compare' && data) {
        return (
            <HorizontalBarChart
                maxValue={overrideMaxValue ? parseInt(`${overrideMaxValue}`) : undefined}
                expectedHeight={expectedHeight ? parseInt(`${expectedHeight}`) : undefined}
                chartData={data}
                chartDataType={chartDataType}
                xAxisLabel={valueAxisLabel}
                yAxisLabel={dataAxisLabel}
                decimalPlace={decimalPlace}
            />
        );
    } else if (selectedVisualisation?.label.toLowerCase() === 'trend' && data) {
        return (
            <VerticalBarChart
                maxValue={overrideMaxValue ? parseInt(`${overrideMaxValue}`) : undefined}
                expectedHeight={expectedHeight ? parseInt(`${expectedHeight}`) : undefined}
                chartData={data}
                chartDataType={chartDataType}
                xAxisLabel={chartDataType === 'participation' ? dataAxisLabel : 'Year'}
                yAxisLabel={valueAxisLabel}
                decimalPlace={decimalPlace}
            />
        );
    } else if (selectedVisualisation?.label.toLowerCase() === 'map' && data) {
        // render Map
        const mapType = splitter?.regionType?.toLowerCase() || 'lga';

        let legendTitle = 'Screening Participation:';
        if (chartDataType === 'colonoscopy') {
            legendTitle = 'Colonoscopy uptake:';
        } else if (chartDataType === 'colposcopy') {
            legendTitle = 'Colposcopy uptake:';
        } else if (chartDataContent === 'cervical') {
            if (chartDataType === 'participation') {
                legendTitle = 'Screening Coverage:';
            } else if (chartDataType === 'selfCollection') {
                legendTitle = 'Self Collection uptake:';
            }
        }

        const showMapLegend = chartDataType === 'participation' || chartDataType === 'selfCollection' || chartDataType === 'colonoscopy' || chartDataType === 'colposcopy';

        return (
            <GeoChart
                chartData={data}
                splitter={splitter}
                filter={filter}
                mapType={mapType}
                googleMapsKey={googleMapsKey}
                chartDataType={chartDataType}
                chartDataContent={chartDataContent}
                showMapLegend={showMapLegend}
                legendTitle={legendTitle}
            />
        );
    }

    return (
        <div className="issue">
            We have trouble displaying data for this chart. Please try again in a few minutes or let us know this issue.
        </div>
    );
};

const ChartDisplayApp = (props: ChartDisplayProps): JSX.Element => {
    const { arProps: { visualisation }, chartHeight, maxValue, googleMapsKey, chartDataType, chartDataContent } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [selectedVisualisation, setSelectedVisualisation] = useState(visualisation[0] || null);

    const [data, setData] = useState<any>({});
    const [chartTitle, setChartTitle] = useState('');
    const [chartNotes, setChartNotes] = useState([]);
    const [splitter, setSplitter] = useState({});
    const [filter, setFilter] = useState({});

    const debouncedRequest = useCallback(debounce(requestParam => {
        const { type, url, query } = requestParam;
        if (IsRunningInFEDEnv()) {
            // use mock data
            let fedDataUrl = url;
            if (type === 'Map') {
                // fedDataUrl
                if (query.RegionType === 'Lphu' || query.RegionType === 'Phn') {
                    fedDataUrl = fedDataUrl.replace('.json', `-${query.RegionType}.json`);
                }
            }

            axios.get(fedDataUrl)
                .then(response => {
                    if (response.data) {
                        const { chartData, lines, notes, title } = response.data;
                        if (type === 'Map' || response.data.data?.datasets) {
                            setData(chartData);
                        } else {
                            const newData = {
                                data: {
                                    datasets: [
                                        ...chartData,
                                        ...(chartData.length > 1 && lines ? lines : [])
                                    ],
                                },
                            };

                            setData(newData);
                        }
                        setChartTitle(title || '');
                        setChartNotes(notes || []);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            axios.get(url, { params: query })
                .then(response => {
                    if (response.data) {
                        const { chartData, lines, notes, title } = response.data;
                        if (type === 'Map' || response.data.data?.datasets) {
                            setData(chartData);
                        } else {
                            const newData = {
                                data: {
                                    datasets: [
                                        ...chartData,
                                        ...(chartData.length > 1 && lines ? lines : [])
                                    ],
                                },
                            };

                            setData(newData);
                        }
                        setChartTitle(title || '');
                        setChartNotes(notes || []);
                    } else {
                        setData(null);
                    }
                })
                .catch(reason => {
                    console.error('Error:', reason);
                    setData(null);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, 100), []);

    const RequestData = useCallback((): void => {
        setIsLoading(true);
        if (selectedVisualisation?.src) {
            const splitterGroups = selectedVisualisation?.splitter?.groups;

            const filteredSplitter = {};
            splitterGroups?.forEach(group => {
                const id = group.identifier;
                const deps = group.dependencySplitter;
                const depsOperator = group.dependencyOperator;
                const depsValue = group.dependencyValue;

                if (id) {
                    if (deps && depsOperator && depsValue) {
                        const splitterDepsValue = (splitter as any)[deps];
                        if (depsOperator === 'eq' && splitterDepsValue === depsValue) {
                            (filteredSplitter as any)[id] = (splitter as any)[id];
                        } else if (splitterDepsValue !== depsValue) {
                            (filteredSplitter as any)[id] = (splitter as any)[id];
                        }
                    } else {
                        (filteredSplitter as any)[id] = (splitter as any)[id];
                    }
                }
            });

            const query = BuildChartQueryFilter({
                visualisationType: selectedVisualisation.label.toLowerCase(),
                splitter: filteredSplitter,
                filter,
            });

            debouncedRequest({
                type: selectedVisualisation.label,
                url: selectedVisualisation.src,
                query,
            });
        } else {
            setTimeout(() => {
                setIsLoading(false);
            }, 500);
        }
    }, [debouncedRequest, selectedVisualisation.label, selectedVisualisation.src, splitter, filter]);

    const resetFilter = (filterGroups?: [ChartVisualisationSplitterGroupProps], newSplitter?: Record<string, string>): void => {
        if (filterGroups) {
            const newFilter = {};
            const latestSplitter = newSplitter ? newSplitter : splitter;
            filterGroups.forEach(group => {
                const { identifier, dependencyValue, dependencyOperator, dependencySplitter } = group;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                let dependencyMatch = !!dependencySplitter && ((latestSplitter?.[dependencySplitter]) === dependencyValue);
                if (dependencyOperator === 'not eq') {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    dependencyMatch = !!dependencySplitter && ((latestSplitter?.[dependencySplitter]) !== dependencyValue);
                }
                if (identifier &&
                    (!dependencySplitter || dependencyMatch)) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    newFilter[identifier] = group.options[0].value;
                }
            });
            setFilter(newFilter);
        }
    };

    const resetSplitter = (visualisationToUse: ChartVisualisationProps): void => {
        const splitterGroups = visualisationToUse.splitter?.groups;
        const newSplitter = {};
        if (splitterGroups) {
            splitterGroups.forEach(group => {
                const { identifier } = group;
                if (identifier) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    newSplitter[identifier] = group.options[0].value;
                }
            });
            setSplitter(newSplitter);
        }

        const filterGroups = visualisationToUse.filter?.groups;
        if (filterGroups) {
            resetFilter(filterGroups, newSplitter);
        }
    };

    const visualisationButtonHandler = (value: ChartVisualisationProps): void => {
        if (selectedVisualisation.label !== value.label) {
            setSelectedVisualisation(value);
            resetSplitter(value);
        }
    };

    const splitterChangeHandler = (identifier: string, value: string): void => {
        const newSplitter = { ...splitter };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        newSplitter[identifier] = value;
        setSplitter(newSplitter);

        resetFilter(selectedVisualisation.filter?.groups, newSplitter);
    };

    const filterChangeHandler = (identifier: string, value: string): void => {
        const newFilter = { ...filter };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        newFilter[identifier] = value;
        setFilter(newFilter);
    };

    useEffect(() => {
        RequestData();
    },
    [selectedVisualisation, RequestData]);

    useEffect(() => {
        resetSplitter(visualisation[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visualisation]);

    let showNotes = (chartTitle || (chartNotes && chartNotes.length > 0));
    if (chartDataType === 'selfCollection') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const dataType = splitter ? splitter['CollectionName'] : undefined; // eslint-disable-line
        if (dataType === '% of self-collection uptake') {
            showNotes = false;
        }
    } else if (chartDataType === 'colonoscopy' || chartDataType === 'colposcopy') {
        showNotes = false;
    }

    return (
        <div className="ar-chart">
            <div className="ar-chart-header">
                <RenderVisualisationButtons
                    visualisation={visualisation}
                    selectedVisualisation={selectedVisualisation}
                    clickHandler={visualisationButtonHandler}
                />
                <RenderVisualisationSplitter
                    selectedVisualisation={selectedVisualisation}
                    splitterValue={splitter}
                    changeHandler={splitterChangeHandler}
                />
                <RenderVisualisationFilter
                    selectedVisualisation={selectedVisualisation}
                    splitter={splitter}
                    filterValue={filter}
                    changeHandler={filterChangeHandler}
                />
            </div>
            <div className="ar-chart-body">
                {isLoading && (<LoadingIndicator />)}
                {!isLoading && (
                    <>
                        <RenderChartBody
                            data={data}
                            selectedVisualisation={selectedVisualisation}
                            maxValue={maxValue}
                            expectedHeight={chartHeight}
                            splitter={splitter}
                            filter={filter}
                            googleMapsKey={googleMapsKey}
                            chartDataType={chartDataType}
                            chartDataContent={chartDataContent}
                        />
                        {showNotes && (
                            <div className="chart-footer">
                                {chartTitle && (
                                    <h3 className="chart-footer-target-total">{chartTitle}</h3>
                                )}
                                {(chartNotes && chartNotes.length > 0) && (
                                    <div className="chart-footer-notes">
                                        <p className="chart-footer-note chart-footer-note-title">
                                            Note:
                                        </p>
                                        {chartNotes.map((note, index) => {
                                            const key = `${index}-${kebabCase(note)}`;
                                            return (
                                                <p key={key} className="chart-footer-note">
                                                    {note}
                                                </p>
                                            );
                                        }
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>

    );
};

document.addEventListener('DOMContentLoaded', () => {
    const initChartPanelReact = (): void => {
        const els = document.getElementsByClassName(`${APP_CLASS}`);
        if (els.length > 0) {
            for (let i = 0; i < els.length; i += 1) {
                const el = els.item(i) as HTMLElement;
                const elProps = el?.dataset.props || '{}';
                const propsObj = JSON.parse(elProps);

                if (el) {
                    ReactDOM.render(
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        <ChartDisplayApp {...propsObj} />,
                        el
                    );
                }
            }
        }
    };

    // delay initiation so chart panel that is used inside other component can work (e.g: in Tab)
    setTimeout(initChartPanelReact, 100);
});
